import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Brain, Salad, Moon, BatteryLow, Sparkles, Flower2, HeartHandshake, ArrowRight } from "lucide-react";

interface SeoLink {
  href: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const FEATURED_LINKS: SeoLink[] = [
  {
    href: "/tdah-dys-enfants-atypiques",
    title: "TDAH, DYS & enfants atypiques",
    description:
      "Accompagner les troubles de l'attention, l'hypersensibilité, les troubles DYS et les émotions de l'enfant.",
    icon: <Sparkles className="w-6 h-6" />,
  },
  {
    href: "/parentalite-burn-out-parental",
    title: "Parentalité & burn-out parental",
    description:
      "Soutenir les parents face à la charge mentale, à l'épuisement parental et aux défis du quotidien familial.",
    icon: <HeartHandshake className="w-6 h-6" />,
  },
];

const SEO_LINKS: SeoLink[] = [
  {
    href: "/naturopathe-stress",
    title: "Stress & charge mentale",
    description: "Apaiser le mental et relâcher la pression au quotidien.",
    icon: <Brain className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-sommeil",
    title: "Sommeil",
    description: "Mieux s'endormir et retrouver des nuits réparatrices.",
    icon: <Moon className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-fatigue",
    title: "Fatigue & manque d'énergie",
    description: "Recharger ses batteries quand l'élan vital s'épuise.",
    icon: <BatteryLow className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-digestion",
    title: "Digestion & confort intestinal",
    description: "Retrouver un ventre léger et une digestion sereine.",
    icon: <Salad className="w-5 h-5" />,
  },
  {
    href: "/fleurs-de-bach",
    title: "Fleurs de Bach",
    description: "Un soutien doux pour traverser les états émotionnels.",
    icon: <Flower2 className="w-5 h-5" />,
  },
];

export const SeoLinksHomeSection: React.FC = () => {
  return (
    <section id="domaines-accompagnement" className="py-12 sm:py-16 md:py-20 bg-muted/20" aria-labelledby="seo-links-title">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              id="seo-links-title"
              className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary uppercase tracking-wide"
            >
              Domaines d'accompagnement
            </h2>
            <div className="w-20 h-px bg-secondary mx-auto mt-4 mb-4" />
            <p className="text-base sm:text-lg text-foreground/70 font-montserrat max-w-2xl mx-auto">
              Choisissez la problématique qui vous concerne pour en savoir plus.
            </p>
          </div>

          <nav aria-label="Pages thématiques">
            {/* Featured cards */}
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6 sm:mb-8">
              {FEATURED_LINKS.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="group block h-full" aria-label={link.title}>
                    <Card className="h-full p-6 sm:p-7 border-2 border-secondary/30 hover:border-secondary/70 hover:shadow-elegant transition-all duration-300 bg-gradient-to-br from-card to-muted/20 hover:-translate-y-0.5 relative overflow-hidden">
                      <div className="flex flex-col h-full">
                        <div className="flex items-start gap-4 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground flex-shrink-0 shadow-md">
                            {link.icon}
                          </div>
                          <h3 className="text-lg sm:text-xl md:text-2xl font-cinzel font-semibold text-primary leading-snug group-hover:text-secondary transition-colors pt-1">
                            {link.title}
                          </h3>
                        </div>
                        <p className="text-sm sm:text-base text-foreground/75 font-montserrat leading-relaxed mb-4">
                          {link.description}
                        </p>
                        <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-montserrat font-semibold text-secondary group-hover:gap-2.5 transition-all">
                          En savoir plus
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Standard cards — top row of 3 */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-4 sm:mb-5">
              {SEO_LINKS.slice(0, 3).map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group block h-full"
                    aria-label={link.title}
                  >
                    <Card className="h-full p-5 border-primary/20 hover:border-primary/50 hover:shadow-elegant transition-all duration-300 bg-card hover:-translate-y-0.5">
                      <div className="flex flex-col h-full">
                        <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground mb-3 flex-shrink-0">
                          {link.icon}
                        </div>
                        <h3 className="text-base sm:text-lg font-cinzel font-semibold text-primary mb-1.5 leading-snug group-hover:text-secondary transition-colors">
                          {link.title}
                        </h3>
                        <p className="text-sm text-foreground/70 font-montserrat leading-relaxed mb-3">
                          {link.description}
                        </p>
                        <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-montserrat font-semibold text-secondary group-hover:gap-2.5 transition-all">
                          En savoir plus
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Standard cards — centered bottom row of 2 */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto">
              {SEO_LINKS.slice(3).map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group block h-full"
                    aria-label={link.title}
                  >
                    <Card className="h-full p-5 border-primary/20 hover:border-primary/50 hover:shadow-elegant transition-all duration-300 bg-card hover:-translate-y-0.5">
                      <div className="flex flex-col h-full">
                        <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground mb-3 flex-shrink-0">
                          {link.icon}
                        </div>
                        <h3 className="text-base sm:text-lg font-cinzel font-semibold text-primary mb-1.5 leading-snug group-hover:text-secondary transition-colors">
                          {link.title}
                        </h3>
                        <p className="text-sm text-foreground/70 font-montserrat leading-relaxed mb-3">
                          {link.description}
                        </p>
                        <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-montserrat font-semibold text-secondary group-hover:gap-2.5 transition-all">
                          En savoir plus
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>

          </nav>
        </div>
      </div>
    </section>
  );
};
